import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    fetchDownloadDicomSeries,
    fetchRequestImageDownload,
} from '~/backend/connector-api/connector-components';
import { extractConnectorErrorReason } from '~/backend/connector-api/connector-error';
import { withRetryOn202 } from '~/backend/connector-api/withRetryOn202';
import { Modality, strToModality } from '~/domain/dicom/modality';
import { Series } from '~/domain/dicom/series';
import { Image3D } from '~/domain/patient/image3d';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    imageListVersionState,
    primaryImageState,
} from '~/state/atoms/image/imageAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import useSelectedPatient from '../patient/useSelectedPatient';
import useBusyIndicator from '../useBusyIndicator';
import useProcessDicomFiles from '../useProcessDicomFiles';
import useViewportEnvironment from '../useViewportEnvironment';
import useViewportSecondaryEnvironment from '../useViewportSecondaryEnvironment';

export type SessionImageInfo = {
    frameOfReferenceUid: string;
    xSize: number;
    ySize: number;
    zSize: number;
    xResMm: number;
    yResMm: number;
    zResMm: number;
    /** Acquisition / creation timestamp from the session source, when known. */
    date?: Date;
};

export type AvailableImage = {
    seriesInstanceUID: string;
    studyInstanceUID: string;
    label: string;
    modality: Modality;
    isLoaded: boolean;
    imageObject?: Image3D; // Only present if loaded
    rawSeries?: Series; // Present for non-constructed series
    sliceCount?: number; // Number of DICOM slices in raw series
    needsReformat?: boolean; // True if series couldn't form Image3D
    sessionImageInfo?: SessionImageInfo; // Geometry from session payload, only for not-yet-downloaded images
};

export default function usePrimaryImage(showSelected = false) {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const patient = useSelectedPatient();
    const sessionData = useRecoilValue(sessionDataAtom);
    const primaryImage = useRecoilValue(primaryImageState);
    const viewManager = useRecoilValue(viewManagerState);
    const { setEnvironmentPrimaryImage, initializeEnvironment } =
        useViewportEnvironment();
    const { showBusy, hideBusy } = useBusyIndicator();
    const { processImportedFiles } = useProcessDicomFiles();
    const { removeEnvironmentSecondaryImageSideBySide } =
        useViewportSecondaryEnvironment();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const imageListVersion = useRecoilValue(imageListVersionState);
    const availableImages = useMemo(() => {
        const combinedImages: AvailableImage[] = [];

        // Add loaded images (from patient)
        const loadedImages = patient
            ? Array.from(patient.images)
                  .filter(
                      (img) =>
                          showSelected ||
                          img.seriesUid !== primaryImage?.seriesUid
                  )
                  .map((img) => ({
                      seriesInstanceUID: img.seriesUid,
                      studyInstanceUID: img.studyUid,
                      label: img.seriesMetadata.seriesDescription,
                      modality: img.seriesMetadata.modality,
                      isLoaded: true,
                      imageObject: img,
                  }))
            : [];
        combinedImages.push(...loadedImages);

        // Collect series UIDs already in DicomProcessor (downloaded
        // but may have failed Image3D construction, e.g. raw series)
        const downloadedSeriesUids = new Set<string>();
        for (const patientDataSet of dicomProcessor.patients) {
            for (const series of patientDataSet.series) {
                downloadedSeriesUids.add(series.seriesInstanceUID);
            }
        }

        // Add session images that haven't been loaded or downloaded yet
        if (sessionData?.payload?.images) {
            const sessionImages = sessionData.payload.images
                .filter(
                    (img) =>
                        img &&
                        !loadedImages.some(
                            (loadedImg) =>
                                loadedImg.seriesInstanceUID ===
                                img.series_instance_uid
                        ) &&
                        !downloadedSeriesUids.has(img.series_instance_uid ?? '')
                )
                .map((img) => {
                    const parsedDate = img.date
                        ? new Date(img.date)
                        : undefined;
                    return {
                        seriesInstanceUID: img.series_instance_uid!,
                        studyInstanceUID: img.study_instance_uid!,
                        modality: strToModality(img.modality ?? ''),
                        label: img.display_name ?? '',
                        isLoaded: false,
                        sliceCount: img.z_size,
                        sessionImageInfo: {
                            frameOfReferenceUid: img.frame_of_reference_uid,
                            xSize: img.x_size,
                            ySize: img.y_size,
                            zSize: img.z_size,
                            xResMm: img.x_res,
                            yResMm: img.y_res,
                            zResMm: img.z_res,
                            date:
                                parsedDate &&
                                !Number.isNaN(parsedDate.getTime())
                                    ? parsedDate
                                    : undefined,
                        },
                    };
                });
            combinedImages.push(...sessionImages);
        }

        // Phase 3: Surface raw series from DicomProcessor that failed
        // Image3D construction (CT/MR only, not PET)
        const loadedSeriesUids = new Set(
            combinedImages.map((img) => img.seriesInstanceUID)
        );
        for (const patientDataSet of dicomProcessor.patients) {
            for (const series of patientDataSet.series) {
                if (!series.is3DImage) {
                    continue;
                }
                const modality = series.seriesModule.modality;
                if (modality !== Modality.CT && modality !== Modality.MR) {
                    continue;
                }
                if (loadedSeriesUids.has(series.seriesInstanceUID)) {
                    continue;
                }
                if (series.numberOfObjects < 2) {
                    continue;
                }
                const firstSlice = series.dicomImageSlices[0];
                combinedImages.push({
                    seriesInstanceUID: series.seriesInstanceUID,
                    studyInstanceUID: firstSlice?.studyInstanceUID ?? '',
                    label:
                        series.seriesModule.seriesDescription ||
                        'Unknown Series',
                    modality,
                    isLoaded: false,
                    rawSeries: series,
                    sliceCount: series.numberOfObjects,
                    needsReformat: true,
                });
            }
        }

        return combinedImages;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        patient,
        primaryImage,
        sessionData,
        showSelected,
        dicomProcessor,
        imageListVersion,
    ]);

    const handleChangePrimaryImage = useCallback(
        async (image: AvailableImage) => {
            // Skip if this image is already the primary
            if (image.seriesInstanceUID === primaryImage?.seriesUid) {
                return;
            }
            if (viewManager?.secondaryEnvironment) {
                removeEnvironmentSecondaryImageSideBySide();
            }
            if (image.imageObject && patient) {
                setEnvironmentPrimaryImage(
                    [image.imageObject],
                    patient?.dataSet.PatientID
                );
            } else if (!image.isLoaded && sessionData?.payload?.patient?.id) {
                await showBusy('Downloading images...');
                try {
                    await fetchRequestImageDownload({
                        body: {
                            patientId: sessionData.payload.patient.id,
                            studyInstanceUID: image.studyInstanceUID,
                            seriesInstanceUID: image.seriesInstanceUID,
                            modality: image.modality,
                        },
                    });
                    const dicomBlob = await withRetryOn202(
                        async () =>
                            await fetchDownloadDicomSeries({
                                pathParams: {
                                    studyId: image.studyInstanceUID,
                                    seriesId: image.seriesInstanceUID,
                                },
                                queryParams: { series: true },
                            })
                    );
                    const files = [
                        new File([dicomBlob], `${image.seriesInstanceUID}.zip`),
                    ];

                    const processedItems = await processImportedFiles(files);
                    if (processedItems) {
                        initializeEnvironment(processedItems);
                    }
                } catch (error) {
                    const reason = extractConnectorErrorReason(error);
                    const message = reason
                        ? intl.formatMessage(
                              { id: 'image.downloadFailedWithReason' },
                              { reason }
                          )
                        : intl.formatMessage({ id: 'image.downloadFailed' });
                    enqueueSnackbar(message, { variant: 'error' });
                } finally {
                    hideBusy();
                }
            }
        },
        [
            primaryImage?.seriesUid,
            enqueueSnackbar,
            hideBusy,
            initializeEnvironment,
            intl,
            patient,
            processImportedFiles,
            removeEnvironmentSecondaryImageSideBySide,
            sessionData?.payload?.patient?.id,
            setEnvironmentPrimaryImage,
            showBusy,
            viewManager?.secondaryEnvironment,
        ]
    );

    const getPrimaryImageGeometry = useCallback((): {
        sliceThickness: number;
        widthMm: number;
    } => {
        if (!primaryImage) {
            return { sliceThickness: 0, widthMm: 0 };
        }
        return {
            sliceThickness: primaryImage.grid.resZ,
            widthMm: primaryImage.grid.sizeXMM,
        };
    }, [primaryImage]);

    return {
        primaryImage,
        availableImages,
        handleChangePrimaryImage,
        getPrimaryImageGeometry,
    };
}
